import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import { auth } from "../../../firebase";
import { buildAvatarImageAttrs } from "../../../utils/imageUrl";
import {
  fetchBusinessById,
  fetchBusinessFollowersCount,
  fetchBusinessSponsoredCapsules,
  followBusiness,
  isFollowingBusiness,
  unfollowBusiness,
} from "../../../services/businessService";
import {
  fetchCampaignOffersForBusinesses,
  getCampaignOfferClaimPath,
} from "../../../services/offersService";
import { useSeoRoute } from "../../../seo";

function formatCapsuleDate(value) {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function normalizeAddressList(business = {}) {
  const addresses = [];
  const hqAddress = String(business?.hqAddress || "").trim();
  if (hqAddress) {
    addresses.push({
      id: "hq",
      label: "HQ",
      address: hqAddress,
      lat: Number(business?.hqLocation?.lat),
      lng: Number(business?.hqLocation?.lng),
      placeId: String(business?.hqPlaceId || "").trim(),
    });
  }

  const locationValues = Array.isArray(business?.locations) ? business.locations : [];
  locationValues.forEach((entry, idx) => {
    if (typeof entry === "string") {
      const address = String(entry || "").trim();
      if (!address || address === hqAddress) return;
      addresses.push({
        id: `location-${idx}`,
        label: "Location",
        address,
        lat: Number.NaN,
        lng: Number.NaN,
        placeId: "",
      });
      return;
    }

    if (!entry || typeof entry !== "object") return;
    const address = String(entry?.address || entry?.formattedAddress || "").trim();
    if (!address || address === hqAddress) return;
    addresses.push({
      id: `location-${idx}`,
      label: "Location",
      address,
      lat: Number(entry?.lat ?? entry?.location?.lat),
      lng: Number(entry?.lng ?? entry?.location?.lng),
      placeId: String(entry?.placeId || "").trim(),
    });
  });

  const detailValues = Array.isArray(business?.locationDetails) ? business.locationDetails : [];
  detailValues.forEach((entry, idx) => {
    if (!entry || typeof entry !== "object") return;
    const address = String(entry?.address || entry?.formattedAddress || "").trim();
    if (!address || addresses.some((item) => item.address === address)) return;
    addresses.push({
      id: `detail-${idx}`,
      label: String(entry?.name || entry?.label || "Location").trim() || "Location",
      address,
      lat: Number(entry?.lat ?? entry?.location?.lat),
      lng: Number(entry?.lng ?? entry?.location?.lng),
      placeId: String(entry?.placeId || "").trim(),
    });
  });

  return addresses;
}

function formatOfferDate(value) {
  const label = formatCapsuleDate(value);
  return label ? `Ends ${label}` : "";
}

function firstImageUrl(...values) {
  const flattened = values.flatMap((value) => {
    if (!value) return [];
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) return value.flatMap((entry) => firstImageUrl(entry));
    if (typeof value === "object") {
      return [
        value.sessionPreviewUrl,
        value.posterUrl,
        value.thumbnailUrl,
        value.imageUrl,
        value.imageURL,
        value.photoUrl,
        value.photoURL,
        value.downloadURL,
        value.url,
      ];
    }
    return [];
  });
  return String(flattened.find((url) => String(url || "").trim()) || "").trim();
}

function campaignTypeLabel(type = "") {
  return String(type || "").toLowerCase() === "coupon" ? "Coupon" : "Raffle";
}

function campaignTypeIcon(type = "") {
  return String(type || "").toLowerCase() === "coupon" ? LocalOfferRoundedIcon : ConfirmationNumberRoundedIcon;
}

function shortAddress(address = "") {
  const parts = String(address || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.slice(0, 2).join(", ") || String(address || "").trim();
}

function normalizeFeaturedOffer(offer = {}) {
  const type = String(offer?.campaignType || "").toLowerCase() === "coupon" ? "coupon" : "raffle";
  const label = campaignTypeLabel(type);
  const Icon = campaignTypeIcon(type);
  return {
    id: `offer-${offer?.campaignId || offer?.id}`,
    rawId: String(offer?.campaignId || offer?.id || "").trim(),
    type,
    label,
    Icon,
    title: String(offer?.title || label).trim(),
    subtitle: formatOfferDate(offer?.claimEndAt) || offer?.locationName || offer?.address || "",
    href: getCampaignOfferClaimPath(offer) || "/claims",
    imageUrl: firstImageUrl(offer?.heroImageUrl, offer?.thumbnailUrl, offer?.imageUrl, offer?.heroImage),
  };
}

function normalizeFeaturedCapsule(capsule = {}) {
  return {
    id: `capsule-${capsule?.id}`,
    rawId: String(capsule?.id || "").trim(),
    type: "capsule",
    label: "Featured",
    Icon: Inventory2RoundedIcon,
    title: String(capsule?.messageTitle || capsule?.title || "Featured time capsule").trim(),
    subtitle: formatCapsuleDate(capsule?.sentAt || capsule?.createdAt) || capsule?.location?.address || "",
    href: `/capsule/${encodeURIComponent(String(capsule?.id || "").trim())}`,
    imageUrl: firstImageUrl(
      capsule?.heroImageUrl,
      capsule?.thumbnailUrl,
      capsule?.imageUrl,
      capsule?.coverImageUrl,
      capsule?.media,
      capsule?.mediaFiles,
      capsule?.previewMedia,
      capsule?.images,
      capsule?.photos
    ),
  };
}

function emptyMessageForTab(tab) {
  if (tab === "coupon") return "No coupons right now.";
  if (tab === "raffle") return "No raffles right now.";
  if (tab === "capsule") return "No featured time capsules yet.";
  return "No featured drops yet.";
}

export default function PublicBusinessPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const currentUid = auth.currentUser?.uid || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [business, setBusiness] = useState(null);
  const [capsules, setCapsules] = useState([]);
  const [offers, setOffers] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);

  useSeoRoute(
    business
      ? {
          id: business.id || businessId,
          title: business.displayName || business.companyName || "Business",
          displayName: business.displayName || business.companyName || "Business",
          companyName: business.companyName || "",
          description: business.tagline || "",
          image: business.avatarUrl || business.logoUrl || "",
          hqAddress: business.hqAddress || "",
        }
      : { id: businessId }
  );

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [businessRecord, sponsoredCapsules, campaignOffers, followingStatus, rawFollowersCount] = await Promise.all([
          fetchBusinessById(businessId),
          fetchBusinessSponsoredCapsules(businessId, { limit: 30 }),
          fetchCampaignOffersForBusinesses([businessId], { limit: 30 }),
          currentUid ? isFollowingBusiness(currentUid, businessId) : Promise.resolve(false),
          fetchBusinessFollowersCount(businessId),
        ]);

        if (!active) return;
        if (!businessRecord) {
          setBusiness(null);
          setCapsules([]);
          setOffers([]);
          setError("Business not found.");
          return;
        }

        setBusiness(businessRecord);
        setCapsules(sponsoredCapsules);
        setOffers(Array.isArray(campaignOffers) ? campaignOffers : []);
        const nextFollowing = Boolean(followingStatus);
        const nextRawFollowersCount = Number(rawFollowersCount) || 0;
        const nextFollowersCount = nextFollowing
          ? Math.max(1, nextRawFollowersCount)
          : nextRawFollowersCount;
        setIsFollowing(nextFollowing);
        setFollowersCount(nextFollowersCount);
      } catch (err) {
        console.error("Failed to load business page:", err);
        if (!active) return;
        setError("Failed to load business page.");
        setBusiness(null);
        setCapsules([]);
        setOffers([]);
        setFollowersCount(0);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();

    return () => {
      active = false;
    };
  }, [businessId, currentUid]);

  useEffect(() => {
    setFollowersCount((prev) => Math.max(0, Number(prev) || 0));
  }, [businessId]);

  useEffect(() => {
    if (isFollowing && followersCount < 1) {
      setFollowersCount(1);
    }
  }, [isFollowing, followersCount]);

  const addressEntries = useMemo(() => normalizeAddressList(business), [business]);
  const avatarAttrs = useMemo(
    () => buildAvatarImageAttrs(business?.avatarUrl, { size: 88 }),
    [business?.avatarUrl]
  );
  const businessName = String(
    business?.displayName || business?.companyName || "Business"
  ).trim();
  const categoryLabels = Array.isArray(business?.businessCategories)
    ? business.businessCategories.filter(Boolean).slice(0, 5)
    : [];
  const hashtags = Array.isArray(business?.hashtags)
    ? business.hashtags.filter(Boolean).slice(0, 8)
    : [];
  const tagline = String(business?.tagline || "").trim();
  const canFollow = Boolean(currentUid);
  const featuredItems = useMemo(() => {
    const offerItems = (Array.isArray(offers) ? offers : [])
      .map(normalizeFeaturedOffer)
      .filter((item) => item.rawId);
    const capsuleItems = (Array.isArray(capsules) ? capsules : [])
      .map(normalizeFeaturedCapsule)
      .filter((item) => item.rawId);
    return [...offerItems, ...capsuleItems];
  }, [capsules, offers]);
  const visibleFeaturedItems = useMemo(() => {
    if (activeTab === "all") return featuredItems;
    return featuredItems.filter((item) => item.type === activeTab);
  }, [activeTab, featuredItems]);
  const tabCounts = useMemo(
    () =>
      featuredItems.reduce(
        (acc, item) => {
          acc.all += 1;
          acc[item.type] = (acc[item.type] || 0) + 1;
          return acc;
        },
        { all: 0, coupon: 0, raffle: 0, capsule: 0, locations: addressEntries.length }
      ),
    [addressEntries.length, featuredItems]
  );
  const chipSpacingSx = {
    "& .MuiChip-label": {
      px: 1.1,
    },
  };

  const handleFollowToggle = async () => {
    if (!currentUid || !business?.id || followBusy) return;
    setFollowBusy(true);
    try {
      if (isFollowing) {
        await unfollowBusiness(currentUid, business.id);
        setIsFollowing(false);
        setFollowersCount((prev) => Math.max(0, (Number(prev) || 0) - 1));
      } else {
        await followBusiness(currentUid, business);
        setIsFollowing(true);
        setFollowersCount((prev) => Math.max(1, (Number(prev) || 0) + 1));
      }
    } catch (err) {
      console.error("Failed to toggle business follow:", err);
    } finally {
      setFollowBusy(false);
    }
  };

  const handleOpenLocation = (entry) => {
    const lat = Number(entry?.lat);
    const lng = Number(entry?.lng);
    const placeAddress = String(entry?.address || "").trim();
    const placeId = String(entry?.placeId || "").trim();

    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      navigate("/", {
        state: {
          center: { lat, lng },
          selectedPlaceName: businessName,
          selectedPlaceAddress: placeAddress,
          selectedPlaceId: placeId,
        },
      });
      return;
    }

    if (placeAddress) {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        placeAddress
      )}`;
      window.open(mapsUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!business) {
    return (
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Alert severity="info">{error || "Business not found."}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 3 } }}>
      <Card
        variant="outlined"
        sx={(theme) => ({
          mb: 1.5,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.colors.mediumBlue, 0.28)} 100%)`
              : "background.paper",
        })}
      >
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Avatar
              src={avatarAttrs.src || undefined}
              imgProps={{
                srcSet: avatarAttrs.srcSet || undefined,
                sizes: avatarAttrs.sizes || undefined,
                loading: "eager",
                decoding: "async",
              }}
              sx={{ width: 88, height: 88, fontSize: 34 }}
            >
              {businessName.charAt(0).toUpperCase() || <BusinessOutlinedIcon />}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.05,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {businessName}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.8 }} alignItems="center">
                <Chip
                  size="small"
                  label={`${followersCount} follower${followersCount === 1 ? "" : "s"}`}
                  variant="outlined"
                  sx={chipSpacingSx}
                />
              </Stack>
            </Box>
          </Stack>

          {categoryLabels.length ? (
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
              {categoryLabels.map((value) => (
                <Chip key={value} size="small" label={value} sx={chipSpacingSx} />
              ))}
            </Stack>
          ) : null}

          {tagline ? (
            <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary" }}>
              {tagline}
            </Typography>
          ) : null}

          {hashtags.length ? (
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
              {hashtags.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  variant="outlined"
                  label={`#${String(tag).replace(/^#+/, "")}`}
                  sx={chipSpacingSx}
                />
              ))}
            </Stack>
          ) : null}

          {canFollow ? (
            <Button
              onClick={handleFollowToggle}
              disabled={followBusy}
              variant="contained"
              startIcon={isFollowing ? <CheckRoundedIcon /> : <AddRoundedIcon />}
              sx={(theme) => ({
                mt: 2,
                minHeight: 42,
                px: 2.2,
                fontWeight: 900,
                backgroundColor: theme.palette.success.main,
                color: "#102215",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.success.main, 0.9),
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              })}
            >
              {isFollowing ? "Following" : "Follow"}
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.6 }}>
        <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
          Featured
        </Typography>
        {tabCounts.all ? (
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
            {tabCounts.all} active
          </Typography>
        ) : null}
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(_, nextValue) => setActiveTab(nextValue)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 1.25 }}
      >
        <Tab value="all" label={`All${tabCounts.all ? ` (${tabCounts.all})` : ""}`} />
        <Tab value="coupon" label={`Coupons${tabCounts.coupon ? ` (${tabCounts.coupon})` : ""}`} />
        <Tab value="raffle" label={`Raffles${tabCounts.raffle ? ` (${tabCounts.raffle})` : ""}`} />
        <Tab value="capsule" label={`Capsules${tabCounts.capsule ? ` (${tabCounts.capsule})` : ""}`} />
        <Tab value="locations" label={`Locations${tabCounts.locations ? ` (${tabCounts.locations})` : ""}`} />
      </Tabs>

      {activeTab === "locations" ? (
        <Card variant="outlined">
          <CardContent>
            {addressEntries.length === 0 ? (
              <Alert severity="info">No locations published yet.</Alert>
            ) : (
              <Stack spacing={1}>
                {addressEntries.map((entry) => (
                  <Button
                    key={entry.id}
                    onClick={() => handleOpenLocation(entry)}
                    variant="outlined"
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      py: 1.1,
                      borderRadius: 1,
                    }}
                    startIcon={<PlaceOutlinedIcon />}
                  >
                    <Box sx={{ textAlign: "left", minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
                        {entry.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.address}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      ) : visibleFeaturedItems.length === 0 ? (
        <Alert severity="info">{emptyMessageForTab(activeTab)}</Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "3px",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {visibleFeaturedItems.map((item) => {
            const Icon = item.Icon || Inventory2RoundedIcon;
            return (
              <ButtonBase
                key={item.id}
                data-testid="business-featured-tile"
                component={RouterLink}
                to={item.href}
                sx={(theme) => ({
                  position: "relative",
                  aspectRatio: "1 / 1",
                  alignItems: "stretch",
                  justifyContent: "stretch",
                  overflow: "hidden",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.common.white, 0.08)
                      : alpha(theme.colors?.paleBlue || theme.colors?.mediumBlue || "#4164D8", 0.08),
                  textDecoration: "none",
                  color: "inherit",
                })}
              >
                {item.imageUrl ? (
                  <Box
                    component="img"
                    src={item.imageUrl}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <Stack alignItems="center" justifyContent="center" sx={{ width: "100%", height: "100%" }}>
                    <Icon sx={(theme) => ({ fontSize: 38, color: theme.palette.success.main })} />
                  </Stack>
                )}
                <Box
                  sx={(theme) => {
                    const useLightPanel = Boolean(item.imageUrl) || theme.palette.mode === "dark";
                    return {
                      position: "absolute",
                      inset: "auto 0 0 0",
                      minHeight: 48,
                      px: 0.75,
                      py: 0.65,
                      background: useLightPanel
                        ? "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.58) 38%, rgba(0,0,0,0.86) 100%)"
                        : "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.88) 28%, rgba(255,255,255,0.98) 100%)",
                    };
                  }}
                >
                  <Stack direction="row" spacing={0.45} alignItems="center" sx={{ minWidth: 0 }}>
                    <Icon sx={{ fontSize: 12, color: "common.white", flexShrink: 0 }} />
                    <Typography
                      sx={{
                        color: "common.white",
                        fontSize: 10.5,
                        fontWeight: 900,
                        lineHeight: 1.15,
                        textAlign: "left",
                        textShadow: "0 1px 2px rgba(0,0,0,0.75)",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      mt: 0.2,
                      color: "rgba(255,255,255,0.96)",
                      fontSize: 10.5,
                      lineHeight: 1.2,
                      textAlign: "left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textShadow: "0 1px 2px rgba(0,0,0,0.75)",
                    }}
                  >
                    {item.title}
                  </Typography>
                  {item.subtitle ? (
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.82)",
                        fontSize: 9.5,
                        lineHeight: 1.15,
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textShadow: "0 1px 2px rgba(0,0,0,0.75)",
                      }}
                    >
                      {shortAddress(item.subtitle)}
                    </Typography>
                  ) : null}
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      )}
    </Container>
  );
}
